import { useEffect, useMemo, useRef, useState } from 'react';
import { balanceOf } from '@day';
import { useStore } from '../app/store';
import { closeDialog, dialogs, meetOptions, roughlyAdds, roughlyAddsAtEnd, roughMeet, type MeetPlan } from '../app/dialogs';
import * as act from '../app/actions';
import { inMinutes, money, reasonWords, streetOf, time } from '../app/words';
import { routeTitle, type Model, type Person } from '../data/model';
import { Av } from './bits';
import { newSession } from '../data/maps';
import { useLookup, type LookedPlace } from './Lookup';
import { Icon } from './Icon';
import { AddWaiting, WaitingDay } from './WaitingDialogs';
import { InviteCourier } from './InviteDialog';

/** The dialogs: Try again on a stop, and a hand-in to confirm or record (control-room-web.html `ask`). */
export function Dialogs({ m }: { m: Model }) {
  const { open } = useStore(dialogs);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => (box.current?.querySelector('[data-first]') as HTMLElement | null)?.focus(), 0);
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeDialog();
      }
    };
    document.addEventListener('keydown', key, true);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', key, true);
    };
  }, [open]);
  if (!open) return null;
  return (
    <div id="modal" className="on" onClick={(e) => e.target === e.currentTarget && closeDialog()}>
      <div className="box" role="dialog" aria-modal="true" ref={box}>
        {open.kind === 'tryAgain' ? (
          <TryAgain m={m} k={open.key} />
        ) : open.kind === 'startPlace' ? (
          <StartPlace m={m} />
        ) : open.kind === 'addWaiting' ? (
          <AddWaiting m={m} />
        ) : open.kind === 'waitingDay' ? (
          <WaitingDay m={m} ids={open.ids} />
        ) : open.kind === 'invite' ? (
          <InviteCourier m={m} />
        ) : (
          <HandIn m={m} riderId={open.riderId} handInId={open.handInId} out={!!open.out} />
        )}
      </div>
    </div>
  );
}

const plus = (msLater: number) => `+${inMinutes(msLater)}`;

function TryAgain({ m, k }: { m: Model; k: string }) {
  const s = m.stop(k);
  const r = s ? m.route(s.routeId) : null;
  const holder = s ? m.person(s.riderId) : null;
  const [way, setWay] = useState<act.TryWay>('tryAgain');
  const [taker, setTaker] = useState<string | null>(null);
  const shop = m.shop;
  const options = useMemo(() => (r ? meetOptions(r, m.now, shop) : []), [r, m.now, shop]);
  const takers = useMemo(() => {
    if (!s?.spot || !holder) return [];
    const out: { p: Person; plan: MeetPlan | null }[] = [];
    for (const p of m.people) {
      if (p.uid === holder.uid) continue;
      const run = p.day?.running;
      const at = run?.position ?? p.day?.presence ?? null;
      if (!at) continue;
      const next = run?.stops[run.current]?.spot ?? null;
      out.push({ p, plan: roughMeet(options, at, next, s.spot, (r?.timePerStopS ?? 180) * 2000) });
    }
    return out.sort((a, b) => (a.plan?.takerAdds ?? 864e5) - (b.plan?.takerAdds ?? 864e5));
  }, [m.people, options, s, holder, r]);
  if (!s || !r || !holder) return <Gone />;
  const left = r.stops.filter((x) => x.state === 'current' || x.state === 'ahead').length;
  const again = s.spot ? roughlyAdds(r, s.spot) : 0;
  const back = shop ? roughlyAddsAtEnd(r, shop) : null;
  const picked = takers.find((t) => t.p.uid === taker) ?? null;
  const send = () => {
    if (way === 'handover') {
      if (!picked?.plan) return;
      const o = picked.plan.option;
      act.tryAgain(k, 'handover', picked.p, { label: o.label, lat: o.at.lat, lng: o.at.lng, holderStopId: o.holderStopId, holderAt: m.now + o.holderIn, takerAt: m.now + picked.plan.takerIn });
    } else if (way === 'bringBack') {
      if (!shop) return;
      act.tryAgain(k, 'bringBack', null, { label: shop.label, lat: shop.lat, lng: shop.lng });
    } else act.tryAgain(k, 'tryAgain');
    closeDialog();
  };
  const label = way === 'handover' ? (picked ? `Send to ${picked.p.first}` : 'Pick who takes it') : way === 'bringBack' ? `Send to ${holder.first}` : `Send to ${holder.first}`;
  return (
    <>
      <div className="eyebrow">
        Try again · Not delivered {time(s.at, m.zone)} · {reasonWords(s.reason)}
      </div>
      <div className="title" style={{ marginTop: 4 }}>
        {streetOf(s)}
      </div>
      <div className="row" style={{ gap: 6, marginTop: 6 }}>
        <Icon name="package" size="sm" />
        <b>The parcel is with {holder.first}.</b>
      </div>
      <div className="picks" role="radiogroup" aria-label="Who takes it">
        <Pick on={way === 'tryAgain'} first onClick={() => setWay('tryAgain')} lead={<Av p={holder} size="sm" />} title={`${holder.first} tries again`} note={`Has it · on a route, ${left} ${left === 1 ? 'stop' : 'stops'} left`} trailing={plus(again)} />
        <Pick
          on={way === 'bringBack'}
          disabled={!shop}
          onClick={() => setWay('bringBack')}
          lead={<Icon name="storefront" />}
          title="Bring it back"
          note={shop ? `${shop.label} as their last stop, then it waits for your next route` : m.place ? `${m.place.label} isn’t on the map yet: it shows once a route has started there` : 'Set where routes start first: Finish setting up, or your name at the top right'}
          trailing={back != null ? plus(back) : undefined}
        />
        <Pick on={way === 'handover'} onClick={() => setWay('handover')} lead={<Icon name="users" />} title="Someone else takes it" note={`They meet ${holder.first} on the way`} />
        {way === 'handover' && (
          <div className="takers">
            {takers.length ? (
              takers.map(({ p, plan }) => (
                <Pick
                  key={p.uid}
                  on={taker === p.uid}
                  onClick={() => setTaker(p.uid)}
                  lead={<Av p={p} size="sm" />}
                  title={p.first}
                  note={plan ? `Meets ${holder.first} at ${plan.option.label}, about ${time(m.now + Math.max(plan.takerIn, plan.option.holderIn), m.zone)}` : 'No meeting place works out'}
                  trailing={plan ? plus(plan.takerAdds) : undefined}
                />
              ))
            ) : (
              <div className="cap" style={{ padding: '6px 4px' }}>
                No one else has a position now: not on a route, and not signed in with Routr open.
              </div>
            )}
            {takers.length > 0 && <div className="cap" style={{ padding: '6px 4px 0' }}>Straight-line estimates. Each courier fits the stop in or re-sorts the rest on their phone.</div>}
          </div>
        )}
      </div>
      <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
        <button className="btn" onClick={closeDialog}>
          Cancel
        </button>
        <button className="btn strong" onClick={send} disabled={(way === 'handover' && !picked?.plan) || (way === 'bringBack' && !shop)}>
          <Icon name="paperPlaneTilt" size="sm" />
          {label}
        </button>
      </div>
      <div className="cap" style={{ marginTop: 10 }}>
        {r ? `From ${holder.first}’s ${routeTitle(r, m.zone)}. ` : ''}The customer’s tracking link carries on{way === 'bringBack' ? ', except to the shop' : ''}.
      </div>
    </>
  );
}

function Pick({ on, first, disabled, onClick, lead, title, note, trailing }: { on: boolean; first?: boolean; disabled?: boolean; onClick: () => void; lead: React.ReactNode; title: string; note: string; trailing?: string }) {
  return (
    <button className={`pick${on ? ' on' : ''}`} role="radio" aria-checked={on} disabled={disabled} onClick={onClick} data-first={first ? '' : undefined}>
      <span className="lead">{lead}</span>
      <span className="grow">
        <b>{title}</b>
        <span className="cap">{note}</span>
      </span>
      {trailing && <span className="mono t">{trailing}</span>}
      {on && <Icon name="check" />}
    </button>
  );
}

function Gone() {
  return (
    <>
      <div className="title">That’s changed</div>
      <p className="ink2">The stop isn’t on today’s routes any more.</p>
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <button className="btn" onClick={closeDialog} data-first="">
          Close
        </button>
      </div>
    </>
  );
}

/** Naira typed as people type them ("12,000", "12000.50"): minor units, or null. */
export function readAmount(text: string): number | null {
  const t = text.replace(/[₦,\s]/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  return Math.round(Number(t) * 100);
}

function HandIn({ m, riderId, handInId, out }: { m: Model; riderId: string; handInId?: string; out: boolean }) {
  const p = m.person(riderId);
  const h = handInId ? m.data.handIns.find((x) => x.id === handInId) : undefined;
  const start = h ? h.amount : Math.abs(p?.balance ?? 0);
  // Naira and kobo exactly: a rounded amount would leave a balance of a few kobo that never settles.
  const [text, setText] = useState(start ? (start / 100).toFixed(start % 100 ? 2 : 0) : '');
  if (!p) return <Gone />;
  const amount = readAmount(text);
  const b = balanceOf(riderId, m.data.routeMoney, m.data.handIns);
  // The same balance as the page and the pre-fill (CourierBalance.of), so "becomes" is what the page shows after.
  const after = amount == null ? null : b.amount - (out ? -amount : amount);
  const verb = out ? `Record paying ${p.first}` : h ? `Confirm ${p.first} handed in` : `Record ${p.first} handing in`;
  const go = () => {
    if (amount == null || amount < 0 || (amount === 0 && !h)) return;
    if (h) act.confirmHandIn(h, amount);
    else act.recordHandIn(riderId, amount, out);
    closeDialog();
  };
  return (
    <>
      <div className="title">
        {verb} {h ? money(h.amount, m.currency) : ''}
      </div>
      <p className="ink2" style={{ margin: '8px 0 14px' }}>
        {h ? `${p.first} says they handed in ${money(h.amount, m.currency)} ${h.how === 'transfer' ? 'by transfer' : 'in cash'}. Confirm what actually came in.` : out ? `What you paid ${p.first} of the fees paid into your account.` : `Money ${p.first} handed over the counter.`}
      </p>
      <label className="field big">
        <span className="mono ink2">{m.currency === 'NGN' ? '₦' : m.currency}</span>
        <input data-first="" inputMode="decimal" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && go()} aria-label="Amount" />
      </label>
      <p className="cap" style={{ margin: '10px 0 18px' }}>
        {after != null ? `${p.first}’s balance becomes ${after === 0 ? '₦0' : after > 0 ? money(after, m.currency) : `you owe ${money(-after, m.currency)}`}. ` : 'Type the amount in naira. '}
        It shows on both your records and can’t be undone; a correction can be added after.
      </p>
      <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
        {h && (
          <button
            className="btn danger"
            onClick={() => {
              act.handInNotReceived(h);
              closeDialog();
            }}
          >
            Not received
          </button>
        )}
        <span className="grow" />
        <button className="btn" onClick={closeDialog}>
          Cancel
        </button>
        <button className="btn strong" onClick={go} disabled={amount == null || (amount === 0 && !h)}>
          {h ? `Confirm ${amount != null ? money(amount, m.currency) : ''}` : `Record ${amount != null ? money(amount, m.currency) : ''}`}
        </button>
      </div>
    </>
  );
}

/**
 * Where routes start (Finish setting up's first step, ProfileService.saveBusinessPlaces): a name and an address, found
 * through Routr's server or typed. The list keeps no position (Google's 30 days); the map's pin comes from the routes.
 */
function StartPlace({ m }: { m: Model }) {
  const cur = m.place;
  const [label, setLabel] = useState(cur?.label ?? m.business?.name ?? '');
  const [q, setQ] = useState(cur?.address ?? '');
  const [picked, setPicked] = useState<{ address: string; placeId?: string } | null>(cur ? { address: cur.address, placeId: cur.placeId } : null);
  const session = useRef(newSession());
  const address = q.trim();
  // Looked up as typing pauses; what was picked, or is already saved, isn't asked again.
  const found = useLookup(address, address.length >= 4 && picked?.address !== address, m.business?.country?.toLowerCase(), session);
  const look = found && found !== 'looking' && found.q === address ? found : null;
  const take = (p: LookedPlace) => {
    setQ(p.address);
    setPicked({ address: p.address, placeId: p.placeId ?? p.pid });
    session.current = newSession();
  };
  const rest = (m.business?.places ?? []).slice(1);
  const save = () => {
    if (!label.trim() || !address) return;
    act.saveStartPlaces([{ label: label.trim(), address, ...(picked?.placeId && picked.address === address ? { placeId: picked.placeId } : {}) }, ...rest]);
    closeDialog();
  };
  return (
    <>
      <div className="title">Where routes start</div>
      <p className="ink2" style={{ margin: '8px 0 14px' }}>Your shop, or wherever couriers collect parcels. Every route you plan starts there, and couriers see its name.</p>
      <label className="field">
        <Icon name="storefront" />
        <input data-first="" placeholder="Its name" value={label} onChange={(e) => setLabel(e.target.value)} autoComplete="off" aria-label="Name" />
      </label>
      <label className="field" style={{ marginTop: 8 }}>
        <Icon name="mapPin" />
        <input
          placeholder="Its address"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && look?.places[0] && take(look.places[0])}
          autoComplete="off"
          spellCheck={false}
          aria-label="Address"
        />
        {found === 'looking' && <span className="cap">Looking…</span>}
      </label>
      {look && (
        <div className="rows" style={{ marginTop: 6 }}>
          {look.places.map((p, i) => (
            <div key={p.pid ?? p.placeId ?? p.address} className={`item${i === 0 ? ' hot' : ''}`} role="button" tabIndex={0} onClick={() => take(p)}>
              <Icon name="mapPin" />
              <span className="grow ellipsis">{p.address}</span>
            </div>
          ))}
          {!look.places.length && <div className="cap" style={{ padding: '6px 2px' }}>{look.error ?? 'Nothing found for that: the address as you’d write it is fine.'}</div>}
        </div>
      )}
      <p className="cap" style={{ margin: '10px 0 18px' }}>Only its name and address are kept. The map shows it where your routes have started from.</p>
      <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
        {cur && (
          <button
            className="btn danger"
            onClick={() => {
              act.saveStartPlaces(rest);
              closeDialog();
            }}
          >
            Remove
          </button>
        )}
        <span className="grow" />
        <button className="btn" onClick={closeDialog}>
          Cancel
        </button>
        <button className="btn strong" onClick={save} disabled={!label.trim() || !address}>
          Save
        </button>
      </div>
    </>
  );
}

